/**
 * A food from its package: take photos (Nutrition Facts, front, ingredients) for the AI to read,
 * or type the label in; either way the values land in one editable Nutrition Facts form with a
 * live True Label check, and nothing is saved until the user confirms. Products with a barcode can
 * be shared with the Mighty Peanut database (reviewed before anyone else sees them), photos included.
 *
 * The same flow corrects a product whose data is wrong or incomplete: the form starts from the
 * values shown, the fix is saved to the user's own copy at once, and the correction (with the
 * photos and a note) goes to the owner's review queue.
 */
import { useMemo, useRef, useState, type ReactElement } from 'react';
import { declaredFrom, LABEL_FIELD_DEFS, type LabelField } from '@engine/label';
import { NUTRIENTS, NUTRIENT_BY_KEY, roundNutrient } from '@engine/nutrients';
import { analyzeLabel, type TrueLabelReport } from '@engine/tef';
import { prepareImage, type PreparedImage } from '../lib/image';
import { errorMessage, log } from '../lib/logger';
import type { Food, FoodInsert, FoodPortion, NutrientMap, NutrientSourceMap, ReadLabelResponse, SubmissionOriginal } from '../lib/types';
import { useApp } from '../state/app';
import { LabelCheckLine } from './Badges';
import { Icon } from './Icon';
import { Spinner } from './ui';

type Part = 'nutrition' | 'front' | 'ingredients';
const PARTS: ReadonlyArray<{ part: Part; title: string; hint: string }> = [
  { part: 'nutrition', title: 'Nutrition Facts', hint: 'Straight on, filling the frame, no glare.' },
  { part: 'front', title: 'Front of the package', hint: 'Name and brand.' },
  { part: 'ingredients', title: 'Ingredients', hint: 'The list and any “Contains:” line.' },
];

const FIELD_LABEL: Readonly<Record<LabelField, string>> = {
  calories: 'Calories', total_fat_g: 'Total fat', saturated_fat_g: 'Saturated fat', trans_fat_g: 'Trans fat',
  cholesterol_mg: 'Cholesterol', sodium_mg: 'Sodium', total_carbohydrate_g: 'Total carbohydrate', dietary_fiber_g: 'Dietary fiber',
  total_sugars_g: 'Total sugars', added_sugars_g: 'Added sugars', sugar_alcohol_g: 'Sugar alcohol', protein_g: 'Protein',
  vitamin_d_mcg: 'Vitamin D', calcium_mg: 'Calcium', iron_mg: 'Iron', potassium_mg: 'Potassium',
};
const FIELD_UNIT = (f: LabelField): string => (f === 'calories' ? 'kcal' : f.endsWith('_mcg') ? 'mcg' : f.endsWith('_mg') ? 'mg' : 'g');
const LABEL_KEYS: ReadonlySet<string> = new Set(LABEL_FIELD_DEFS.map((d) => d.key));
/** Catalog nutrients a label doesn't print, offered under "More nutrients" (e.g. for vitamins). */
const EXTRA_NUTRIENTS = NUTRIENTS.filter((n) => !LABEL_KEYS.has(n.key) && n.key !== 'water_g' && n.key !== 'ash_g');

export interface LabelFormState {
  name: string;
  brand: string;
  servingLabel: string;
  servingGrams: string;
  servings: string;
  values: Partial<Record<LabelField, string>>;
  extras: Record<string, string>;
  ingredients: string;
  allergens: string;
}

export const EMPTY_LABEL_FORM: LabelFormState = { name: '', brand: '', servingLabel: '', servingGrams: '', servings: '', values: {}, extras: {}, ingredients: '', allergens: '' };

export function formFromRead(r: ReadLabelResponse, fallbackName: string = ''): LabelFormState {
  const values: Partial<Record<LabelField, string>> = {};
  for (const [f, v] of Object.entries(r.label.perServing) as Array<[LabelField, number]>) {
    values[f] = r.label.lessThanOne.includes(f) ? '<1' : String(v);
  }
  return {
    name: r.product.name ?? fallbackName, brand: r.product.brand ?? '',
    servingLabel: r.label.servingSizeText ?? '', servingGrams: r.label.servingSizeG !== null ? String(r.label.servingSizeG) : '',
    servings: r.label.servingsPerContainer !== null ? String(r.label.servingsPerContainer) : '',
    values, extras: {}, ingredients: r.ingredients ?? '', allergens: r.allergens.join(', '),
  };
}

/** A per-serving amount as a label would print it. */
function printed(v: number, unit: string): string {
  const r: number = unit === 'kcal' || unit === 'mg' || v >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
  return String(r);
}

/**
 * The form for a product whose values are already known (per 100 g), for one serving. Nutrients a
 * label doesn't print go under "More nutrients", so a correction keeps them.
 */
export function formFromValues(v: {
  name: string; brand: string | null; per100g: NutrientMap; serving: { label: string | null; grams: number } | null; ingredients?: string | null; allergens?: string[];
}): LabelFormState {
  const grams: number = v.serving?.grams ?? 100;
  const values: Partial<Record<LabelField, string>> = {};
  for (const d of LABEL_FIELD_DEFS) {
    const x: number | undefined = v.per100g[d.key];
    if (x !== undefined) values[d.field] = printed((x * grams) / (100 * d.factor), FIELD_UNIT(d.field));
  }
  const extras: Record<string, string> = {};
  for (const n of EXTRA_NUTRIENTS) {
    const x: number | undefined = v.per100g[n.key];
    if (x !== undefined) extras[n.key] = String(roundNutrient((x * grams) / 100));
  }
  return {
    name: v.name, brand: v.brand ?? '', servingLabel: v.serving?.label ?? (v.serving ? '' : '100 g'), servingGrams: String(grams), servings: '',
    values, extras, ingredients: v.ingredients ?? '', allergens: (v.allergens ?? []).join(', '),
  };
}

/** Label values just read from photos, over what the form already held (name, brand and extras stay when the photos lack them). */
function mergeRead(base: LabelFormState, read: LabelFormState): LabelFormState {
  return {
    ...read,
    name: read.name || base.name, brand: read.brand || base.brand,
    servingLabel: read.servingLabel || base.servingLabel, servingGrams: read.servingGrams || base.servingGrams, servings: read.servings || base.servings,
    extras: { ...base.extras, ...read.extras }, ingredients: read.ingredients || base.ingredients, allergens: read.allergens || base.allergens,
  };
}

/** A correction of a product that is already known. */
export interface CorrectionInfo {
  /** The user's own copy to overwrite; null makes a new one (with the barcode). */
  ownFoodId: number | null;
  /** The shared food it corrects; null for an Open Food Facts product or the user's own food. */
  targetFoodId: number | null;
  barcode: string | null;
  original: SubmissionOriginal;
  /** Portions of the own copy other than the printed serving, kept on save. */
  otherPortions?: FoodPortion[];
}

async function blobOf(img: PreparedImage): Promise<Blob> {
  return (await fetch(img.dataUrl)).blob();
}

interface Parsed {
  perServing: Partial<Record<LabelField, number>>;
  lessThanOne: LabelField[];
  servingG: number | null;
  servings: number | null;
  extras: NutrientMap;
  problem: string | null;
}

export function parseForm(f: LabelFormState): Parsed {
  const perServing: Partial<Record<LabelField, number>> = {};
  const lessThanOne: LabelField[] = [];
  let problem: string | null = null;
  for (const d of LABEL_FIELD_DEFS) {
    const raw: string = (f.values[d.field] ?? '').trim().replace(/\s*(g|mg|mcg|kcal)$/i, '');
    if (raw === '') continue;
    if (/^<\s*1$/.test(raw) || /^less than 1$/i.test(raw)) {
      if (d.lessThanOneAllowed) { perServing[d.field] = 0.5; lessThanOne.push(d.field); continue; }
      problem ??= `${FIELD_LABEL[d.field]}: “<1” is only used for carbs, fiber, sugars and protein. Type the number (US labels print under 0.5 g of fat as 0).`;
      continue;
    }
    const v: number = Number(raw);
    if (!Number.isFinite(v) || v < 0) { problem ??= `${FIELD_LABEL[d.field]} must be a number (or <1).`; continue; }
    if (v > d.max) { problem ??= `${FIELD_LABEL[d.field]} of ${v} per serving looks wrong.`; continue; }
    perServing[d.field] = v;
  }
  const g: number = Number(f.servingGrams);
  const servingG: number | null = f.servingGrams.trim() !== '' && Number.isFinite(g) && g > 0 && g <= 5000 ? g : null;
  const sp: number = Number(f.servings);
  const servings: number | null = f.servings.trim() !== '' && Number.isFinite(sp) && sp > 0 && sp <= 1000 ? sp : null;
  const extras: NutrientMap = {};
  for (const [k, raw] of Object.entries(f.extras)) {
    if (raw.trim() === '') continue;
    const v: number = Number(raw);
    if (!Number.isFinite(v) || v < 0) { problem ??= `${NUTRIENT_BY_KEY.get(k)?.name ?? k} must be a number of 0 or more.`; continue; }
    extras[k] = v;
  }
  return { perServing, lessThanOne, servingG, servings, extras, problem };
}

/** Per 100 g catalog values from a parsed form. */
export function per100From(p: Parsed): NutrientMap {
  if (p.servingG === null) return {};
  const out: NutrientMap = {};
  for (const d of LABEL_FIELD_DEFS) {
    const v: number | undefined = p.perServing[d.field];
    if (v !== undefined) out[d.key] = roundNutrient((v * d.factor * 100) / p.servingG);
  }
  for (const [k, v] of Object.entries(p.extras)) out[k] = roundNutrient((v * 100) / p.servingG);
  return out;
}

function reportFor(p: Parsed): TrueLabelReport | null {
  const declared = declaredFrom({ found: true, servingSizeText: null, servingSizeG: p.servingG, servingsPerContainer: p.servings, perServing: p.perServing, lessThanOne: p.lessThanOne, unreadable: [] });
  if (typeof declared === 'string') return null;
  try { return analyzeLabel(declared); } catch { return null; }
}

/** The editable Nutrition Facts form with a live True Label check. Calls onSave with the new food. */
export function LabelReview({ initial, barcode, aiModel, warnings = [], via, onSaved, onBack, photos = [], correction = null }: {
  initial: LabelFormState;
  barcode: string | null;
  aiModel: string | null;
  warnings?: string[];
  /** How the values got here, for the food's notes. */
  via: 'photos' | 'typed';
  onSaved: (food: Food) => void;
  onBack?: () => void;
  /** Package photos, sent with a shared submission. */
  photos?: PreparedImage[];
  correction?: CorrectionInfo | null;
}): ReactElement {
  const { source, toast } = useApp();
  const [f, setF] = useState<LabelFormState>(initial);
  const shareable: boolean = barcode !== null || (correction?.targetFoodId ?? null) !== null;
  const [share, setShare] = useState<boolean>(shareable);
  const [note, setNote] = useState<string>('');
  const [more, setMore] = useState<boolean>(correction !== null && Object.keys(initial.extras).length > 0);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const parsed: Parsed = useMemo(() => parseForm(f), [f]);
  const report: TrueLabelReport | null = useMemo(() => reportFor(parsed), [parsed]);
  const set = (k: keyof LabelFormState, v: string): void => setF((x) => ({ ...x, [k]: v }));
  const setVal = (field: LabelField, v: string): void => setF((x) => ({ ...x, values: { ...x.values, [field]: v } }));

  const save = async (): Promise<void> => {
    setError(null);
    if (f.name.trim() === '') { setError('Give the food a name.'); return; }
    if (parsed.problem) { setError(parsed.problem); return; }
    if (parsed.servingG === null) { setError('Enter the serving weight in grams (the number in brackets after the serving size, e.g. “1 bar (60g)”).'); return; }
    if (parsed.perServing.calories === undefined) { setError('Enter the calories per serving.'); return; }
    const per100: NutrientMap = per100From(parsed);
    const kcal100: number | undefined = per100.energy_kcal;
    if (kcal100 !== undefined && kcal100 > 902) { setError('Those calories are more than pure fat per gram: check the serving weight.'); return; }
    const fiber: number = per100.fiber_g ?? 0;
    if (per100.carbs_g !== undefined && fiber > per100.carbs_g + 0.5) { setError('Fiber can’t be more than total carbohydrate.'); return; }
    const sources: NutrientSourceMap = Object.fromEntries(Object.keys(per100).map((k) => [k, via === 'typed' ? 'user' : 'label'])) as NutrientSourceMap;
    const allergens: string[] = f.allergens.split(',').map((a) => a.trim().toLowerCase()).filter((a) => a !== '').slice(0, 40);
    const notes: string = [
      barcode ? `Barcode ${barcode}.` : '',
      via === 'photos' ? 'Read from package photos and checked by you.' : 'Typed from the label.',
      allergens.length > 0 ? `Allergens: ${allergens.join(', ')}.` : '',
      f.ingredients.trim() ? `Ingredients: ${f.ingredients.trim()}` : '',
    ].filter(Boolean).join(' ').slice(0, 2000);
    const servingLabel: string = (f.servingLabel.trim() || `1 serving (${parsed.servingG} g)`).slice(0, 120);
    const serving: FoodPortion = { label: servingLabel, grams: Math.round(parsed.servingG * 100) / 100 };
    const insert: FoodInsert = {
      source: barcode ? 'barcode' : 'user',
      ...(barcode ? { barcode } : {}),
      name: f.name.trim().slice(0, 300),
      brand: f.brand.trim() ? f.brand.trim().slice(0, 120) : null,
      category: null,
      nutrients: per100,
      nutrient_sources: sources,
      portions: [serving, ...(correction?.otherPortions ?? []).filter((p) => p.label !== servingLabel)].slice(0, 20),
      notes: correction ? `${notes} Corrected by you.`.trim().slice(0, 2000) : notes,
      ai_model: via === 'photos' ? aiModel : null,
    };
    if (correction && share && note.trim() === '' && photos.length === 0) {
      setError('Add a photo of the label or say what was wrong, so the fix can be checked.');
      return;
    }
    setBusy(true);
    try {
      // A correction lands on the user's own copy: the one given, else the one saved with this barcode.
      let ownId: number | null = correction?.ownFoodId ?? null;
      if (correction && ownId === null && barcode) {
        const mine: Food | null = await source.findFoodByBarcode(barcode).catch(() => null);
        if (mine && mine.owner_id !== null) ownId = mine.id;
      }
      const food: Food = ownId !== null
        ? await source.updateFood(ownId, {
          name: insert.name, brand: insert.brand, nutrients: insert.nutrients, nutrient_sources: insert.nutrient_sources, portions: insert.portions, notes: insert.notes,
          ...(barcode ? { barcode } : {}),
        })
        : await source.createFood(insert);
      let sent: boolean = false;
      let photoProblem: string | null = null;
      if (share) {
        const paths: string[] = [];
        for (const img of photos.slice(0, 6)) {
          try { paths.push(await source.uploadFoodPhoto(await blobOf(img))); } catch (err) { photoProblem = errorMessage(err); }
        }
        const label: Record<string, number | string[]> = { ...parsed.perServing, less_than_one: parsed.lessThanOne };
        sent = await source.submitFood({
          barcode, name: insert.name, brand: insert.brand, serving_label: servingLabel, serving_grams: parsed.servingG,
          servings_per_container: parsed.servings, label, nutrients: per100, ingredients: f.ingredients.trim() || null,
          allergens, ai_model: via === 'photos' ? aiModel : null, label_check: report?.verdict ?? null, food_id: food.id,
          kind: correction ? 'correction' : 'new',
          ...(correction ? { target_food_id: correction.targetFoodId, original: correction.original } : {}),
          user_note: note.trim() ? note.trim().slice(0, 1000) : null,
          photo_paths: paths,
        });
        if (!sent) log.info('food.share_skipped', { barcode });
      }
      const saved: string = correction ? `Fixed in My foods: ${food.name}` : `Saved to My foods: ${food.name}`;
      if (share && !sent) toast(`${saved}. It could not be sent for review right now.`, 'err');
      else if (share && photoProblem) toast(`${saved}. Sent for review, but a photo didn't upload: ${photoProblem}`, 'err');
      else toast(share ? `${saved}. Sent for review.` : saved);
      onSaved(food);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const valueInput = (field: LabelField): ReactElement => (
    <label key={field} className="field"><span>{FIELD_LABEL[field]}</span>
      <div className="input-suffix">
        <input className="input" inputMode="decimal" value={f.values[field] ?? ''} placeholder={['total_carbohydrate_g', 'dietary_fiber_g', 'total_sugars_g', 'added_sugars_g', 'sugar_alcohol_g', 'protein_g'].includes(field) ? '<1 is OK' : ''}
          onChange={(e) => setVal(field, e.target.value)} />
        <em>{FIELD_UNIT(field)}</em>
      </div>
    </label>
  );

  return (
    <div className="stack" style={{ gap: 14 }}>
      {warnings.length > 0 && <div className="note warn"><ul style={{ margin: 0 }}>{warnings.map((w) => <li key={w}>{w}</li>)}</ul></div>}
      <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <label className="field"><span>Name</span><input className="input" value={f.name} maxLength={300} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Chocolate peanut butter protein bar" /></label>
        <label className="field"><span>Brand</span><input className="input" value={f.brand} maxLength={120} onChange={(e) => set('brand', e.target.value)} /></label>
      </div>
      <div className="form-grid">
        <label className="field"><span>Serving size (as printed)</span><input className="input" value={f.servingLabel} maxLength={120} onChange={(e) => set('servingLabel', e.target.value)} placeholder="e.g. 1 bar (60g)" /></label>
        <label className="field"><span>Serving weight</span><div className="input-suffix"><input className="input" type="number" min={0} step="any" inputMode="decimal" value={f.servingGrams} onChange={(e) => set('servingGrams', e.target.value)} /><em>g</em></div><small>Drinks: use mL.</small></label>
        <label className="field"><span>Servings per container</span><input className="input" type="number" min={0} step="any" inputMode="decimal" value={f.servings} onChange={(e) => set('servings', e.target.value)} /></label>
      </div>
      <div>
        <div className="field-title">Per serving, exactly as printed (leave a line empty if the label doesn’t have it)</div>
        <div className="form-grid label-grid">{LABEL_FIELD_DEFS.map((d) => valueInput(d.field))}</div>
      </div>

      {report ? <LabelCheckLine report={report} /> : <p className="faint" style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>The True Label check runs once calories, fat, carbs and protein are filled in.</p>}

      <details open={more} onToggle={(e) => setMore((e.target as HTMLDetailsElement).open)}>
        <summary className="cite" style={{ cursor: 'pointer', fontWeight: 700 }}>More nutrients (optional, per serving): vitamins, minerals, caffeine…</summary>
        {more && (
          <div className="form-grid label-grid" style={{ marginTop: 8 }}>
            {EXTRA_NUTRIENTS.map((n) => (
              <label key={n.key} className="field"><span>{n.name}</span>
                <div className="input-suffix"><input className="input" inputMode="decimal" value={f.extras[n.key] ?? ''} onChange={(e) => setF((x) => ({ ...x, extras: { ...x.extras, [n.key]: e.target.value } }))} /><em>{n.unit}</em></div>
              </label>
            ))}
          </div>
        )}
      </details>

      <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <label className="field"><span>Ingredients (optional)</span><textarea className="input" value={f.ingredients} maxLength={4000} onChange={(e) => set('ingredients', e.target.value)} style={{ minHeight: 70 }} /></label>
        <label className="field"><span>Allergens (optional)</span><input className="input" value={f.allergens} onChange={(e) => set('allergens', e.target.value)} placeholder="milk, soy" /></label>
      </div>
      {correction !== null && (
        <label className="field"><span>What was wrong? (helps the reviewer)</span>
          <textarea className="input" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} style={{ minHeight: 60 }}
            placeholder="e.g. Calories were per 100 g, not per bar. Protein was missing." />
        </label>
      )}
      {shareable && (
        <label className="check" style={{ fontSize: 13.5 }}>
          <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} />
          {correction
            ? `Send this fix to the Mighty Peanut food database${photos.length > 0 ? ` with ${photos.length} photo${photos.length === 1 ? '' : 's'}` : ''} (checked before anyone else sees it)`
            : `Share this product with the Mighty Peanut food database${photos.length > 0 ? ` with ${photos.length} photo${photos.length === 1 ? '' : 's'}` : ''} (checked before anyone else sees it)`}
        </label>
      )}
      {error !== null && <div className="note err">{error}</div>}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        {onBack && <button type="button" className="btn" onClick={onBack} disabled={busy}>Back</button>}
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>{busy ? <Spinner /> : <Icon name="check" size={18} />} {correction ? 'Save the fix' : 'Save to My foods'}</button>
      </div>
    </div>
  );
}

/**
 * Photos first, then the review form. `barcode` is the scanned code (or null). With `correction`,
 * the form starts from `initial` (the values shown before) and photos are optional.
 */
export function LabelPhotoFlow({ barcode, onSaved, onCancel, initial = null, correction = null }: {
  barcode: string | null;
  onSaved: (food: Food) => void;
  onCancel: () => void;
  initial?: LabelFormState | null;
  correction?: CorrectionInfo | null;
}): ReactElement {
  const { source } = useApp();
  const [photos, setPhotos] = useState<Partial<Record<Part, PreparedImage>>>({});
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<{ form: LabelFormState; warnings: string[]; model: string | null; via: 'photos' | 'typed' } | null>(null);
  const inputs = useRef<Partial<Record<Part, HTMLInputElement | null>>>({});
  const taken: PreparedImage[] = PARTS.map(({ part }) => photos[part]).filter((p): p is PreparedImage => p !== undefined);

  const pick = async (part: Part, file: File | undefined): Promise<void> => {
    if (!file) return;
    setError(null);
    try {
      const img: PreparedImage = await prepareImage(file, 1600, 0.85);
      setPhotos((p) => ({ ...p, [part]: img }));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const read = async (): Promise<void> => {
    if (!photos.nutrition && !photos.front) { setError('Take a photo of the Nutrition Facts panel (and the front if you can).'); return; }
    setBusy(true);
    setError(null);
    try {
      const photo = (p: Part) => (photos[p] ? { data: photos[p]!.dataUrl, mimeType: photos[p]!.mimeType } : undefined);
      const r: ReadLabelResponse = await source.readLabel({
        nutrition: photo('nutrition'), front: photo('front'), ingredients: photo('ingredients'), ...(barcode ? { barcode } : {}),
      });
      const form: LabelFormState = initial ? mergeRead(initial, formFromRead(r)) : formFromRead(r);
      setReview({ form, warnings: r.warnings, model: r.model, via: 'photos' });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (review) {
    return <LabelReview initial={review.form} barcode={barcode} aiModel={review.model} warnings={review.warnings} via={review.via} onSaved={onSaved}
      onBack={() => setReview(null)} photos={taken} correction={correction} />;
  }

  return (
    <div className="stack" style={{ gap: 14 }}>
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>
        {correction
          ? <>Photograph the package so the fix can be checked, and the label can be read for you. Or skip the photos and change the numbers yourself. Your fix is used for you right away; everyone else gets it once it’s approved.</>
          : <>{barcode ? <>Barcode <b>{barcode}</b> isn’t in the database yet. </> : null}Take up to three photos and the label is read for you. You check every number before anything is saved.</>}
      </p>
      <div className="photo-slots">
        {PARTS.map(({ part, title, hint }) => (
          <div key={part} className={`photo-slot ${photos[part] ? 'has' : ''}`}>
            <button type="button" className="photo-drop" onClick={() => inputs.current[part]?.click()}>
              {photos[part] ? <img src={photos[part]!.dataUrl} alt={title} /> : <Icon name="camera" size={28} />}
            </button>
            <div className="photo-title">{title}{part === 'nutrition' ? ' (most important)' : ''}</div>
            <small className="faint">{photos[part] ? 'Tap to retake' : hint}</small>
            <input ref={(el) => { inputs.current[part] = el; }} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void pick(part, e.target.files?.[0]); e.target.value = ''; }} />
          </div>
        ))}
      </div>
      {error !== null && <div className="note err">{error}</div>}
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button type="button" className="btn btn-ghost" onClick={() => setReview({ form: initial ?? EMPTY_LABEL_FORM, warnings: [], model: null, via: 'typed' })}>
          {correction ? (taken.length > 0 ? 'Change the numbers myself' : 'Skip photos, change the numbers myself') : 'Type the label in myself instead'}
        </button>
        <div className="row">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => void read()} disabled={busy || (!photos.nutrition && !photos.front)}>{busy ? <Spinner /> : <Icon name="sparkle" size={18} />} {busy ? 'Reading…' : 'Read the label'}</button>
        </div>
      </div>
    </div>
  );
}
